import { apiFetch } from '@/api/apiFetch';
import { useState } from 'react';
import { Mail, Copy, Check, Send, Clock, CheckCircle2 } from 'lucide-react';

export default function InviteManager() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [generatedLink, setGeneratedLink] = useState('');
  const [copied, setCopied] = useState(false);
  const [invites, setInvites] = useState([]);
  // Email al que se acaba de enviar la invitación. Vacío cuando el envío falló
  // y hay que caer al enlace manual de `generatedLink`.
  const [emailSentTo, setEmailSentTo] = useState('');

  async function handleGenerate(e) {
    e.preventDefault();
    setError('');
    setGeneratedLink('');
    setLoading(true);
    try {
      const data = await apiFetch('/api/auth/invite', {
        method: 'POST',
        body: JSON.stringify({ email }),
      });
      // El enlace solo vuelve si el correo NO salió. En el caso normal ya está
      // en el buzón del invitado y aquí basta con confirmar el envío.
      setEmailSentTo(data.email_sent ? email : '');
      setGeneratedLink(data.link || '');
      setInvites((prev) => [{ email, link: data.link || null, email_sent: !!data.email_sent, used: false, created_at: new Date().toISOString() }, ...prev]);
      setEmail('');
    } catch (err) {
      setError(err.message || 'Error al generar invitación');
    } finally {
      setLoading(false);
    }
  }

  async function handleCopy(link) {
    await navigator.clipboard.writeText(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <div style={{ maxWidth: 680, margin: '0 auto', padding: '32px 24px', fontFamily: 'var(--font-ui)' }}>
      <h1 style={{ fontSize: 22, fontWeight: 700, color: '#0f172a', margin: '0 0 4px', fontFamily: 'var(--font-display)' }}>
        Invitaciones
      </h1>
      <p style={{ fontSize: 14, color: '#64748b', margin: '0 0 32px' }}>
        Genera links de acceso para nuevos usuarios. Cada link es válido 48 horas y de un solo uso.
      </p>

      {/* Generate form */}
      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 14, padding: '24px', marginBottom: 24 }}>
        <form onSubmit={handleGenerate} style={{ display: 'flex', gap: 12, alignItems: 'flex-end' }}>
          <div style={{ flex: 1 }}>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>
              Email del invitado
            </label>
            <div style={{ position: 'relative' }}>
              <Mail style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', width: 16, height: 16, color: '#2563eb', pointerEvents: 'none' }} />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="nuevo@empresa.com"
                required
                style={{ width: '100%', height: 44, paddingLeft: 40, paddingRight: 12, borderRadius: 10, border: '1px solid rgba(0,0,0,0.1)', background: '#f8faff', fontSize: 14, color: '#0f172a', boxSizing: 'border-box', outline: 'none' }}
                onFocus={(e) => { e.target.style.borderColor = '#2563eb'; e.target.style.boxShadow = '0 0 0 3px rgba(37,99,235,0.1)'; }}
                onBlur={(e) => { e.target.style.borderColor = 'rgba(0,0,0,0.1)'; e.target.style.boxShadow = 'none'; }}
              />
            </div>
          </div>
          <button
            type="submit"
            disabled={loading}
            style={{ height: 44, paddingLeft: 20, paddingRight: 20, borderRadius: 10, border: 'none', background: 'linear-gradient(135deg, #2563eb, #1d4ed8)', color: '#fff', fontSize: 14, fontWeight: 600, cursor: loading ? 'wait' : 'pointer', display: 'flex', alignItems: 'center', gap: 8, opacity: loading ? 0.7 : 1, flexShrink: 0 }}
          >
            <Send style={{ width: 15, height: 15 }} />
            Generar
          </button>
        </form>

        {error && (
          <div style={{ marginTop: 12, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 14px', color: '#dc2626', fontSize: 13 }}>
            {error}
          </div>
        )}

        {emailSentTo && (
          <div style={{ marginTop: 16, background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: '14px 16px' }}>
            <p style={{ fontSize: 11, fontWeight: 600, color: '#16a34a', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 6px' }}>Invitación enviada</p>
            <p style={{ fontSize: 13, color: '#166534', margin: 0 }}>
              Le hemos escrito a <strong>{emailSentTo}</strong> con el enlace para crear su cuenta. Caduca en 48 horas.
            </p>
          </div>
        )}

        {generatedLink && (
          <div style={{ marginTop: 16, background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 10, padding: '14px 16px' }}>
            {/* Solo se llega aquí si el correo no salió: sin decirlo, copiar un
                enlace a mano parece un paso normal del flujo y no lo es. */}
            <p style={{ fontSize: 11, fontWeight: 600, color: '#0369a1', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 8px' }}>No se pudo enviar el correo — pásale este enlace</p>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <code style={{ flex: 1, fontSize: 12, color: '#0f172a', background: '#e0f2fe', padding: '6px 10px', borderRadius: 6, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {generatedLink}
              </code>
              <button
                onClick={() => handleCopy(generatedLink)}
                style={{ height: 34, paddingLeft: 14, paddingRight: 14, borderRadius: 8, border: '1px solid #7dd3fc', background: copied ? '#dcfce7' : '#fff', color: copied ? '#16a34a' : '#0369a1', fontSize: 13, fontWeight: 500, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}
              >
                {copied ? <><Check style={{ width: 14, height: 14 }} /> Copiado</> : <><Copy style={{ width: 14, height: 14 }} /> Copiar</>}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* History */}
      {invites.length > 0 && (
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 14, overflow: 'hidden' }}>
          <div style={{ padding: '16px 20px', borderBottom: '1px solid #f1f5f9' }}>
            <h2 style={{ fontSize: 14, fontWeight: 600, color: '#0f172a', margin: 0 }}>Invitaciones enviadas</h2>
          </div>
          {invites.map((inv, i) => (
            <div key={i} style={{ padding: '14px 20px', borderBottom: i < invites.length - 1 ? '1px solid #f1f5f9' : 'none', display: 'flex', alignItems: 'center', gap: 12 }}>
              {inv.used
                ? <CheckCircle2 style={{ width: 18, height: 18, color: '#16a34a', flexShrink: 0 }} />
                : <Clock style={{ width: 18, height: 18, color: '#f59e0b', flexShrink: 0 }} />
              }
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ fontSize: 14, fontWeight: 500, color: '#0f172a', margin: '0 0 2px' }}>{inv.email}</p>
                <p style={{ fontSize: 12, color: '#94a3b8', margin: 0 }}>
                  {inv.used ? 'Usado' : 'Pendiente'} · {new Date(inv.created_at).toLocaleString('es-ES')}
                </p>
              </div>
              {/* Sin enlace no hay nada que copiar: el correo salió y el token
                  ya no vuelve por API. Copiar aquí habría puesto la cadena
                  "null" en el portapapeles. */}
              {inv.link ? (
                <button
                  onClick={() => handleCopy(inv.link)}
                  style={{ height: 30, paddingLeft: 12, paddingRight: 12, borderRadius: 6, border: '1px solid #e2e8f0', background: '#f8faff', color: '#475569', fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0 }}
                >
                  <Copy style={{ width: 12, height: 12 }} /> Copiar link
                </button>
              ) : (
                <span style={{ fontSize: 12, color: '#16a34a', display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0 }}>
                  <Mail style={{ width: 12, height: 12 }} /> Enviado por correo
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
